import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ExecutiveDashboard from "@/pages/ExecutiveDashboard";
import CompaniesList from "@/pages/CompaniesList";
import CompanyDashboard from "@/pages/CompanyDashboard";
import VCReporting from "@/pages/VCReporting";
import DataDictionary from "@/pages/DataDictionary";
import UsersPage from "@/pages/UsersPage";
import FinesPage from "@/pages/FinesPage";
import UploadPage from "@/pages/UploadPage";
import RecordsPage from "@/pages/RecordsPage";
import EtlJobs from "@/pages/EtlJobs";
import ConnectorsAdmin from "@/pages/ConnectorsAdmin";
import MyDashboards from "@/pages/MyDashboards";
import DashboardBuilder from "@/pages/DashboardBuilder";
import DashboardWorkspace from "@/pages/DashboardWorkspace";
import Reports from "@/pages/Reports";
import Inbox from "@/pages/Inbox";
import "@/App.css";

// OAuth callback is served at a dedicated path but Emergent returns via the
// hash fragment (#session_id=...) that can land on any route — this wrapper
// catches that regardless of the current path and forwards to the callback.
function HashOAuthCatcher({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return children;
}

function App() {
  // The Emergent platform script (`emergent-main.js`) rewrites document.title
  // at runtime to "Emergent | Fullstack App". Re-set it on mount AND watch
  // for any further mutations so our custom title sticks.
  useEffect(() => {
    const APP_TITLE = "TCP Data Analytics";
    document.title = APP_TITLE;
    const titleEl = document.querySelector("title");
    if (!titleEl) return;
    const observer = new MutationObserver(() => {
      if (document.title !== APP_TITLE) {
        document.title = APP_TITLE;
      }
    });
    observer.observe(titleEl, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <HashOAuthCatcher>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />

              <Route
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<ExecutiveDashboard />} />
                <Route path="/companies" element={<CompaniesList />} />
                <Route path="/companies/:companyId" element={<CompanyDashboard />} />
                <Route path="/records" element={<RecordsPage />} />
                <Route path="/fines" element={<FinesPage />} />
                <Route path="/my-dashboards" element={<MyDashboards />} />
                <Route path="/workspace" element={<DashboardWorkspace />} />
                <Route path="/dashboards/builder" element={<DashboardBuilder />} />
                <Route path="/dashboards/builder/:dashboardId" element={<DashboardBuilder />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/inbox" element={<Inbox />} />
                <Route
                  path="/vc-reporting"
                  element={
                    <ProtectedRoute roles={["SUPER_ADMIN", "EXEC"]}>
                      <VCReporting />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/upload"
                  element={
                    <ProtectedRoute roles={["SUPER_ADMIN", "EXEC"]}>
                      <UploadPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/data-dictionary"
                  element={
                    <ProtectedRoute roles={["SUPER_ADMIN"]}>
                      <DataDictionary />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/etl"
                  element={
                    <ProtectedRoute roles={["SUPER_ADMIN"]}>
                      <EtlJobs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/connectors"
                  element={
                    <ProtectedRoute roles={["SUPER_ADMIN"]}>
                      <ConnectorsAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute roles={["SUPER_ADMIN"]}>
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </HashOAuthCatcher>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
